import { useState } from "react";
import { useEditorStore } from "@/store";
import { LAYER_TYPE_COLORS, LAYER_TYPE_ICONS, ZOOM_RANGE, type LayerType } from "@/styles/theme";

export function LayerStack() {
  const layers = useEditorStore((s) => s.style.layers);
  const currentZoom = useEditorStore((s) => s.viewport.zoom);
  const selectedLayerId = useEditorStore((s) => s.selectedLayerId);
  const setSelectedLayerId = useEditorStore((s) => s.setSelectedLayerId);
  const setHoveredLayerId = useEditorStore((s) => s.setHoveredLayerId);
  const toggleLayerVisibility = useEditorStore((s) => s.toggleLayerVisibility);
  const updateLayerCommon = useEditorStore((s) => s.updateLayerCommon);
  const setEditorMode = useEditorStore((s) => s.setEditorMode);
  const setActiveSection = useEditorStore((s) => s.setActiveSection);

  const [editingZoomId, setEditingZoomId] = useState<string | null>(null);

  const zoomPct = ((currentZoom - ZOOM_RANGE.min) / (ZOOM_RANGE.max - ZOOM_RANGE.min)) * 100;

  const handleSelectLayer = (id: string) => {
    setSelectedLayerId(id);
    setEditorMode("sections");
    setActiveSection("layers");
  };

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* Header */}
      <div className="flex shrink-0 items-center justify-between px-spacing-panel py-2 border-b border-border-subtle">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-text-muted">
          Layer Stack
        </h2>
        <span className="rounded-radius-full bg-accent-muted px-2 py-0.5 text-[10px] font-medium text-accent">
          z{currentZoom.toFixed(1)}
        </span>
      </div>

      {/* Zoom scale */}
      <div className="relative shrink-0 mx-spacing-panel mt-2 mb-1">
        <div className="flex justify-between text-[9px] text-text-muted pl-28">
          <span>0</span>
          <span>6</span>
          <span>12</span>
          <span>18</span>
          <span>24</span>
        </div>
      </div>

      {/* Layers (reversed: top layer at top) */}
      <div className="flex-1 overflow-y-auto px-spacing-panel pb-2">
        {[...layers].reverse().map((layer) => {
          const layerType = layer.type as LayerType;
          const minZ = layer.minzoom ?? ZOOM_RANGE.min;
          const maxZ = layer.maxzoom ?? ZOOM_RANGE.max;
          const isVisible = layer.layout?.visibility !== "none";
          const isInRange = currentZoom >= minZ && currentZoom < maxZ;
          const isEditingZoom = editingZoomId === layer.id;

          const barLeft = (minZ / ZOOM_RANGE.max) * 100;
          const barWidth = ((maxZ - minZ) / ZOOM_RANGE.max) * 100;

          return (
            <div key={layer.id}>
              <div
                className={`group flex items-center gap-1.5 rounded-radius-sm py-1 pr-1 transition-colors ${
                  selectedLayerId === layer.id ? "bg-accent-muted" : "hover:bg-surface-sunken"
                }`}
                onMouseEnter={() => setHoveredLayerId(layer.id)}
                onMouseLeave={() => setHoveredLayerId(null)}
              >
                {/* Type icon */}
                <span
                  className="flex h-4 w-4 shrink-0 items-center justify-center rounded-sm text-[8px] text-white"
                  style={{ backgroundColor: LAYER_TYPE_COLORS[layerType] }}
                >
                  {LAYER_TYPE_ICONS[layerType]}
                </span>

                {/* Name */}
                <button
                  type="button"
                  onClick={() => handleSelectLayer(layer.id)}
                  className="w-24 shrink-0 truncate text-left text-[10px] text-text-primary"
                >
                  {layer.id}
                </button>

                {/* Zoom range bar — click to edit */}
                <div
                  className="relative h-3 flex-1 cursor-pointer rounded-radius-sm bg-surface-sunken"
                  onClick={() => setEditingZoomId(isEditingZoom ? null : layer.id)}
                  title="Click to edit zoom range"
                >
                  <div
                    className="absolute top-0 h-full rounded-radius-sm transition-opacity"
                    style={{
                      left: `${barLeft}%`,
                      width: `${barWidth}%`,
                      backgroundColor: LAYER_TYPE_COLORS[layerType],
                      opacity: isVisible && isInRange ? 0.7 : 0.2,
                    }}
                  />
                  <div
                    className="absolute top-0 h-full w-px bg-text-primary/40"
                    style={{ left: `${zoomPct}%` }}
                  />
                </div>

                {/* Visibility */}
                <button
                  type="button"
                  onClick={() => toggleLayerVisibility(layer.id)}
                  className={`shrink-0 text-[10px] ${isVisible ? "text-text-muted" : "text-error"}`}
                  title={isVisible ? "Visible" : "Hidden"}
                >
                  {isVisible ? "◉" : "○"}
                </button>
              </div>

              {/* Inline zoom editor */}
              {isEditingZoom && (
                <div className="ml-6 flex items-center gap-1.5 py-1 pl-24">
                  <span className="text-[9px] text-text-muted">z</span>
                  <input
                    type="number"
                    value={minZ}
                    onChange={(e) => {
                      const v = Math.max(0, Math.min(Number(e.target.value), maxZ));
                      updateLayerCommon(layer.id, "minzoom", v);
                    }}
                    min={0}
                    max={maxZ}
                    step={1}
                    className="h-5 w-10 rounded-radius-sm border border-border-subtle bg-surface-base px-1 text-center text-[9px] text-text-primary focus:border-accent focus:outline-none"
                  />
                  <span className="text-[9px] text-text-muted">—</span>
                  <input
                    type="number"
                    value={maxZ}
                    onChange={(e) => {
                      const v = Math.max(minZ, Math.min(Number(e.target.value), 24));
                      updateLayerCommon(layer.id, "maxzoom", v);
                    }}
                    min={minZ}
                    max={24}
                    step={1}
                    className="h-5 w-10 rounded-radius-sm border border-border-subtle bg-surface-base px-1 text-center text-[9px] text-text-primary focus:border-accent focus:outline-none"
                  />
                  <button
                    type="button"
                    onClick={() => setEditingZoomId(null)}
                    className="text-[9px] text-text-muted hover:text-text-primary"
                  >
                    ✓
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
